import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import {
  useMyGroupWinnerPicks,
  useQualificationProjection,
  useSubmitGroupWinnerPrediction,
} from '@/features/games/predictors';
import { useCompetitions } from '@/features/leagues/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { QualGroupTeam, QualStatus } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { localizeRound, localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

const WC_PROVIDER_ID = 9900001;

function statusColor(status: QualStatus, palette: ReturnType<typeof useThemePalette>): string {
  switch (status) {
    case 'qualified':
      return palette.win;
    case 'third_in':
      return palette.voltBright;
    case 'third_out':
      return palette.captain;
    default:
      return palette.inactive;
  }
}

/** A read-only row in the "going through" projection: crest, name, group + rank. */
function ThroughRow({ team, group }: { team: QualGroupTeam; group: string }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, numeralOptions } = useLocale();
  const color = statusColor(team.status, palette);
  const best = team.status === 'third_in';
  return (
    <View className="flex-row items-center gap-2.5 px-3 py-2.5">
      <View style={{ width: 3, height: 22, borderRadius: 2, backgroundColor: color }} />
      <TeamCrest uri={team.logo} name={team.name} size="sm" />
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
          {localizeTeamName({ name: team.name }, locale)}
        </AppText>
        <AppText variant="micro" tabular className="text-muted">
          {`${t('qualification.groupShort', { group: localizeRound(group, locale) })} · ${t('qualification.rankShort', {
            rank: formatNumber(team.rank, numeralOptions),
          })}`}
        </AppText>
      </View>
      <View className="rounded-full px-2 py-0.5" style={{ backgroundColor: `${color}22` }}>
        <AppText variant="micro" weight="bold" style={{ color, fontSize: 9 }}>
          {t(best ? 'qualification.bestThird' : 'qualification.qualified')}
        </AppText>
      </View>
    </View>
  );
}

/** A titled card grouping a set of advancing teams, with a count chip. */
function ThroughSection({
  title,
  count,
  rows,
  tint,
}: {
  title: string;
  count: number;
  rows: { team: QualGroupTeam; group: string }[];
  tint: string;
}) {
  const { numeralOptions } = useLocale();
  return (
    <View>
      <View className="mx-4 mb-1.5 flex-row items-center justify-between">
        <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
          {title}
        </AppText>
        <View className="rounded-full px-2 py-0.5" style={{ backgroundColor: `${tint}22` }}>
          <AppText variant="micro" weight="bold" tabular style={{ color: tint, fontSize: 10 }}>
            {formatNumber(count, numeralOptions)}
          </AppText>
        </View>
      </View>
      <Card className="mx-4 overflow-hidden">
        {rows.map(({ team, group }, i) => (
          <View key={team.team_id} className={i > 0 ? 'border-t border-line' : ''}>
            <ThroughRow team={team} group={group} />
          </View>
        ))}
      </Card>
    </View>
  );
}

function TeamRow({
  team,
  picked,
  locked,
  onPick,
}: {
  team: QualGroupTeam;
  picked: boolean;
  locked: boolean;
  onPick: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale, numeralOptions } = useLocale();
  const color = statusColor(team.status, palette);
  return (
    <Pressable
      onPress={onPick}
      disabled={locked}
      className="flex-row items-center gap-2.5 px-3 py-2.5 active:opacity-70"
      style={picked ? { backgroundColor: palette.voltGlass } : undefined}
    >
      <View style={{ width: 3, height: 22, borderRadius: 2, backgroundColor: color }} />
      <AppText variant="caption" weight="bold" tabular className="w-4 text-center text-muted">
        {formatNumber(team.rank, numeralOptions)}
      </AppText>
      <TeamCrest uri={team.logo} name={team.name} size="xs" />
      <AppText variant="caption" weight={picked ? 'bold' : 'medium'} className="flex-1 text-ink" numberOfLines={1}>
        {localizeTeamName({ name: team.name }, locale)}
      </AppText>
      <View className="rounded-full px-1.5 py-0.5" style={{ backgroundColor: `${color}22` }}>
        <AppText variant="micro" weight="bold" style={{ color, fontSize: 9 }}>
          {t(`qualification.status.${team.status}`)}
        </AppText>
      </View>
      <Ionicons
        name={picked ? 'checkmark-circle' : 'ellipse-outline'}
        size={20}
        color={picked ? palette.volt : palette.inactive}
      />
    </Pressable>
  );
}

export default function QualificationScreen() {
  const { t } = useTranslation();
  const { locale, numeralOptions } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const { userId } = useAuth();
  const howto = useHowToPlay('qualification');
  const comps = useCompetitions();
  const wc = useMemo(
    () => (comps.data ?? []).find((c) => c.provider_id === WC_PROVIDER_ID),
    [comps.data],
  );
  const proj = useQualificationProjection(wc?.id, wc?.current_season ?? undefined);
  const savedQ = useMyGroupWinnerPicks(wc?.id);
  const submit = useSubmitGroupWinnerPrediction();

  // team_id -> group_label, to map saved picks back onto groups.
  const teamGroup = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of proj.data ?? []) for (const tm of g.teams) m.set(tm.team_id, g.group_label);
    return m;
  }, [proj.data]);

  // Picks are derived: the server's saved picks overlaid with this session's
  // local changes - no effect/hydration needed.
  const savedPicks = useMemo(() => {
    const m: Record<string, string> = {};
    for (const tid of savedQ.data?.team_ids ?? []) {
      const g = teamGroup.get(tid);
      if (g) m[g] = tid;
    }
    return m;
  }, [savedQ.data, teamGroup]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const picks = useMemo(() => ({ ...savedPicks, ...overrides }), [savedPicks, overrides]);

  const [mode, setMode] = useState<'through' | 'predict'>('through');

  const scored = savedQ.data?.points != null;
  const groups = proj.data ?? [];
  const pickedCount = Object.keys(picks).length;

  // The 32 advancing teams, split into the two ways a team gets through:
  // top-2 of its group ('qualified') vs one of the best third-placed sides.
  const through = useMemo(() => {
    const qualified: { team: QualGroupTeam; group: string }[] = [];
    const bestThird: { team: QualGroupTeam; group: string }[] = [];
    for (const g of proj.data ?? []) {
      for (const tm of g.teams) {
        if (tm.status === 'qualified') qualified.push({ team: tm, group: g.group_label });
        else if (tm.status === 'third_in') bestThird.push({ team: tm, group: g.group_label });
      }
    }
    return { qualified, bestThird };
  }, [proj.data]);
  const throughCount = through.qualified.length + through.bestThird.length;

  const onPick = (group: string, teamId: string) => {
    if (scored) return;
    setOverrides((p) => ({ ...p, [group]: teamId }));
  };

  const onSave = () => {
    if (!userId) {
      toast.show({ message: t('qualification.signIn'), tone: 'info' });
      return;
    }
    if (!wc) return;
    submit.mutate(
      { competitionId: wc.id, teamIds: Object.values(picks) },
      {
        onSuccess: () => {
          void savedQ.refetch();
          toast.show({ message: t('qualification.saved'), tone: 'success' });
        },
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      },
    );
  };

  const loading = comps.isPending || proj.isLoading;

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="qualification" icon="podium" />
      <BackHeader title={t('qualification.title')} right={<HowToPlayButton onPress={howto.open} />} />
      <View className="pt-1">
        <Segmented
          options={[
            { value: 'through', label: t('qualification.tabThrough'), icon: 'trophy-outline' },
            { value: 'predict', label: t('qualification.tabPredict'), icon: 'podium-outline' },
          ]}
          value={mode}
          onChange={setMode}
        />
      </View>
      <ScrollView contentContainerStyle={{ paddingBottom: 36, gap: 12 }}>
        <AppText variant="caption" className="mx-4 mt-1 text-center text-soft">
          {t(mode === 'through' ? 'qualification.throughIntro' : 'qualification.predictIntro')}
        </AppText>

        {mode === 'predict' && scored ? (
          <Card className="mx-4 flex-row items-center justify-center gap-2 px-4 py-3">
            <Ionicons name="trophy" size={16} color={palette.captain} />
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('qualification.scoredLine', { pts: formatNumber(savedQ.data?.points ?? 0, numeralOptions) })}
            </AppText>
          </Card>
        ) : null}

        {loading ? (
          <View className="gap-3 px-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </View>
        ) : proj.isError ? (
          <ErrorState error={proj.error} onRetry={() => proj.refetch()} />
        ) : mode === 'through' ? (
          throughCount === 0 ? (
            <EmptyState
              icon="trophy-outline"
              title={t('qualification.throughEmptyTitle')}
              body={t('qualification.throughEmptyBody')}
            />
          ) : (
            <>
              <ThroughSection
                title={t('qualification.qualifiedTitle')}
                count={through.qualified.length}
                rows={through.qualified}
                tint={palette.win}
              />
              {through.bestThird.length > 0 ? (
                <ThroughSection
                  title={t('qualification.bestThirdTitle')}
                  count={through.bestThird.length}
                  rows={through.bestThird}
                  tint={palette.voltBright}
                />
              ) : null}
            </>
          )
        ) : groups.length === 0 ? (
          <EmptyState
            icon="podium-outline"
            title={t('qualification.emptyTitle')}
            body={t('qualification.emptyBody')}
          />
        ) : (
          groups.map((group) => (
            <View key={group.group_label}>
              <AppText variant="caption" weight="bold" className="mx-4 mb-1.5 uppercase tracking-wide text-soft">
                {localizeRound(group.group_label, locale)}
              </AppText>
              <Card className="mx-4 overflow-hidden">
                {group.teams.map((team, i) => (
                  <View key={team.team_id} className={i > 0 ? 'border-t border-line' : ''}>
                    <TeamRow
                      team={team}
                      picked={picks[group.group_label] === team.team_id}
                      locked={scored}
                      onPick={() => onPick(group.group_label, team.team_id)}
                    />
                  </View>
                ))}
              </Card>
            </View>
          ))
        )}
      </ScrollView>

      {mode === 'predict' && groups.length > 0 && !scored ? (
        <View className="border-t border-line px-4 py-3" style={{ backgroundColor: palette.deep }}>
          <View className="mb-2 items-center">
            <AppText variant="micro" className="text-muted">
              {t('qualification.progress', {
                picked: formatNumber(pickedCount, numeralOptions),
                total: formatNumber(groups.length, numeralOptions),
              })}
            </AppText>
          </View>
          <Button label={t('qualification.save')} onPress={onSave} loading={submit.isPending} disabled={pickedCount === 0} />
        </View>
      ) : null}
    </Screen>
  );
}
